import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getApiUrl } from '@/lib/apiUrls';
import { canManageInquiries } from '@/utils/roles';
import { VIP_SPEND_LINE_DOLLARS } from '@/constants/vip';

/**
 * VIP customers (src/constants/vip.js) for the staff badge that follows them
 * around the admin pages. Read once per page load from
 * GET /api/admin/vip-customers, and only for staff (admins and the concierge
 * desk): a customer's browser never asks, so nobody can see who else is VIP
 * (a customer sees only their own badge, src/hooks/useMyVip.js).
 */

const EMPTY = () => ({ loaded: false, lineDollars: VIP_SPEND_LINE_DOLLARS, vips: new Map(), byEmail: new Map(), keptUntil: new Map() });
let state = EMPTY();
let pending = null;
const listeners = new Set();

function publish(next) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

async function load() {
  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    if (!session?.access_token || !canManageInquiries(session.user)) {
      publish({ ...state, loaded: true });
      return;
    }
    const res = await fetch(getApiUrl('admin/vip-customers'), {
      headers: { Authorization: `Bearer ${session.access_token}` },
      credentials: 'omit',
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body?.ok) throw new Error(body?.error || `Request failed (${res.status})`);
    const customers = body.customers || [];
    publish({
      loaded: true,
      lineDollars: Number(body.line_dollars) || VIP_SPEND_LINE_DOLLARS,
      vips: new Map(customers.map((c) => [c.user_id, Number(c.spend) || 0])),
      byEmail: new Map(customers.filter((c) => c.email).map((c) => [String(c.email).toLowerCase(), c.user_id])),
      keptUntil: new Map(customers.filter((c) => c.kept_until).map((c) => [c.user_id, c.kept_until])),
    });
  } catch {
    // The badge is a nicety: without the list, nobody shows as VIP.
    publish({ ...state, loaded: true });
  }
}

let watchingAuth = false;
/** A staff member who signs in after the page loaded gets the list then. */
function watchAuth() {
  if (watchingAuth) return;
  watchingAuth = true;
  try {
    supabase.auth.onAuthStateChange?.((event) => {
      if (event === 'SIGNED_IN' && !state.vips.size) pending = load();
    });
  } catch {
    // No auth events (tests, native boot): the first read stands.
  }
}

/**
 * { loaded, lineDollars, vips: Map(user_id → spend), byEmail: Map(email → user_id),
 *   keptUntil: Map(user_id → ISO, when their 12 months end) }.
 */
export function useVipCustomers() {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    listeners.add(setSnapshot);
    if (!pending) pending = load();
    watchAuth();
    setSnapshot(state);
    return () => { listeners.delete(setSnapshot); };
  }, []);
  return snapshot;
}

/**
 * This customer when they're VIP, else null: { spend, keptUntil, lineDollars }.
 * By account id (one, or several for a customer the page has merged), or by
 * email for pages that only know that.
 */
export function useVipEntry({ userId = null, userIds = null, email = null } = {}) {
  const { vips, byEmail, keptUntil, lineDollars } = useVipCustomers();
  const key = email ? String(email).trim().toLowerCase() : '';
  const id = [userId, ...(userIds || []), key ? byEmail.get(key) : null].find((x) => x && vips.has(x));
  return id ? { spend: vips.get(id), keptUntil: keptUntil.get(id) || null, lineDollars } : null;
}

/** This customer's spend when they're VIP, else null (see useVipEntry). */
export function useVipSpend(who = {}) {
  return useVipEntry(who)?.spend ?? null;
}

/** Tests only: forget the loaded list. */
export function resetVipCustomers() {
  state = EMPTY();
  pending = null;
}
